import { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { 
  TextField, 
  Button, 
  Stack,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Autocomplete,
  Skeleton,
  Drawer,
  IconButton,
  Box,
  Divider,
  CircularProgress,
  Chip,
  Link
} from '@mui/material';
import { Visibility, Close, OpenInNew } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { showErrorToast } from 'src/utils/Toast/SnackbarProvider';
import apiInstance from 'src/utils/apiAuth';
import { getDDMMYYYYFormat } from 'src/utils/commonUtils';
import UserInfo from 'src/utils/Authorization/UserInfo';

export default function PerformanceScorecard() {
  const userInfo = UserInfo();
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [viewType, setViewType] = useState('user');
  const [dataType, setDataType] = useState('call'); // 'call' or 'company'
  const [userFilter, setUserFilter] = useState('all');
  const [teamFilter, setTeamFilter] = useState('all');
  const [countryFilter, setCountryFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(false);
  const [performanceData, setPerformanceData] = useState(null);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [countries, setCountries] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [callDetails, setCallDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const buildQuery = () => {
    const params = new URLSearchParams();
    if (fromDate) params.append('start_date', getDDMMYYYYFormat(fromDate));
    if (toDate) params.append('end_date', getDDMMYYYYFormat(toDate));
    if (viewType) params.append('type', viewType);
    if (dataType) params.append('data_type', dataType);
    if (userFilter !== 'all') params.append('users', userFilter);
    if (teamFilter !== 'all') params.append('teams', teamFilter);
    if (countryFilter !== 'all') params.append('countries', countryFilter);
    const query = params.toString();
    return query ? `?${query}` : '';
  };  

  const getPerformanceData = () => {
    setIsLoading(true);
    apiInstance
      .get(`/performance-dashboard/${buildQuery()}`, { headers: { Authorization: `Token ${localStorage.getItem('authToken')}` } })
      .then((response) => {
        if (response?.data?.data) {
          setPerformanceData(response.data.data);
          if (!viewType && response.data.data?.type) {
            setViewType(response.data.data.type);
          }
        }
      })
      .catch((error) => {
        if (error) {
          showErrorToast(error);
        }
      })
      .finally(() => setIsLoading(false));
  };

  const fetchUsers = async () => {
    try {
      const res = await apiInstance.get('/users-viewset?short_details=true');
      setUsers(res.data?.data || []);
    } catch (e) {
      showErrorToast('Failed to load users');
    }
  };

  const fetchTeams = async () => {
    try {
      const res = await apiInstance.get('/teams/');
      setTeams(res.data?.data || []);
    } catch (e) {
      showErrorToast('Failed to load teams');
    }
  };

  const fetchCountries = async () => {
    try {
      const res = await apiInstance.get('/location?active=true&company_added=true');
      setCountries(res.data?.data || []);
    } catch (e) {
      showErrorToast('Failed to load countries');
    }
  }

  useEffect(() => {
    fetchUsers();
    fetchTeams();
    fetchCountries();
  }, []);

  useEffect(() => {
    setPerformanceData(null);
    getPerformanceData();
  }, [fromDate, toDate, viewType, dataType, userFilter, teamFilter, countryFilter]);

  const handleViewDetails = (row) => {
    setSelectedUser(row);
    setDrawerOpen(true);
    setLoadingDetails(true);
    setCallDetails(null);
    
    const params = new URLSearchParams();
    params.append('user_id', row.id);
    if (fromDate) params.append('start_date', getDDMMYYYYFormat(fromDate));
    if (toDate) params.append('end_date', getDDMMYYYYFormat(toDate));
    if (teamFilter !== 'all') params.append('teams', teamFilter);
    if (countryFilter !== 'all') params.append('countries', countryFilter);
    
    apiInstance
      .get(`/performance-dashboard/call-details/?${params.toString()}`, { 
        headers: { Authorization: `Token ${localStorage.getItem('authToken')}` } 
      })
      .then((response) => {
        if (response?.data?.data) {
          setCallDetails(response.data.data);
        }
      })
      .catch((error) => {
        console.error('Error fetching call details:', error);
        showErrorToast('Failed to load call details');
      })
      .finally(() => {
        setLoadingDetails(false);
      });
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedUser(null);
    setCallDetails(null);
  };

  // Filter columns based on data type
  const filteredColumns = performanceData?.columns?.filter(column => {
    const columnName = column.toLowerCase();
    
    // Always exclude "Companies Created" from filtered columns (it's shown separately)
    if (columnName.includes('companies created')) {
      return false;
    }
    
    // Exclude 0% and 5% stages - they are hidden/empty
    if (columnName.match(/\b0%$/) || columnName.match(/\b5%$/)) {
      return false;
    }
    
    return true;
  }) || [];

  // Get column background colors
  const getColumnColor = (index) => {
    const colors = [
      '#f8fafc', // slate-50
      '#eef2ff', // indigo-50
      '#eff6ff', // blue-50
      '#faf5ff', // purple-50
      '#fefce8', // yellow-50
      '#fff7ed', // orange-50
      '#f0fdf4', // green-50
      '#ecfdf5', // emerald-50
    ];
    return colors[index % colors.length];
  };

  return (
    <Stack spacing={2} sx={{ px: 2 }}>
      {/* Header with Filters */}
      <Paper sx={{ p: 2 }}>
        {/* Title and Data Type Tabs */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Performance
            </Typography>
            {/* Data Type Tabs */}
            <Stack direction="row" spacing={1}>
              <Button 
                variant={dataType === 'call' ? 'contained' : 'outlined'} 
                size="small" 
                onClick={() => setDataType('call')}
                sx={{ minWidth: 100 }}
              >
                By Call
              </Button>
              <Button 
                variant={dataType === 'company' ? 'contained' : 'outlined'} 
                size="small" 
                onClick={() => setDataType('company')}
                sx={{ minWidth: 100 }}
              >
                By Company
              </Button>
            </Stack>
          </Stack>
          {/* View Buttons */}
          <Stack direction="row" spacing={1} sx={{ mb: { xs: 1, sm: 0 } }}>
            <Button variant={viewType === 'user' ? 'contained' : 'outlined'} size="small" onClick={() => { setViewType('user'); }}>User View</Button>
            {userInfo && userInfo?.role?.includes("SUPER_ADMIN") && (
              <>
                <Button variant={viewType === 'team' ? 'contained' : 'outlined'} size="small" onClick={() => { setViewType('team'); }}>Team View</Button>
                <Button variant={viewType === 'country' ? 'contained' : 'outlined'} size="small" onClick={() => { setViewType('country'); }}>Country View</Button>
              </>
            )}
          </Stack>
        </Stack>

        {/* Filter Controls */}
        <Stack
          direction="row"
          spacing={1.5}
          mt={2}
          sx={{
            alignItems: 'stretch',
            width: '100%',
            '& > *': { flex: 1, minWidth: 160 },
            '& .MuiInputBase-root': { height: 40 },
            '& .MuiFormLabel-root': { lineHeight: 1.2 }
          }}
        >
          {/* Date Range */}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="From Date"
              value={fromDate}
              onChange={(newVal) => setFromDate(newVal)}
              format="DD-MM-YYYY"
              slotProps={{ textField: { size: 'small', fullWidth: true, InputLabelProps: { shrink: true } } }}
              sx={{ '& .MuiInputBase-root': { height: 40 } }}
            />
          </LocalizationProvider>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="To Date"
              value={toDate}
              onChange={(newVal) => setToDate(newVal)}
              format="DD-MM-YYYY"
              slotProps={{ textField: { size: 'small', fullWidth: true, InputLabelProps: { shrink: true } } }}
              sx={{ '& .MuiInputBase-root': { height: 40 } }}
            />
          </LocalizationProvider>

          {userInfo && userInfo?.role?.includes("SUPER_ADMIN") && (
            <>
              {/* Users filter */}
              <Autocomplete
                options={users}
                value={users.find(u => u.pk === userFilter) || null}
                onChange={(e, value) => setUserFilter(value?.pk || 'all')}
                getOptionLabel={(option) => option.first_name + ' ' + option.last_name}
                renderInput={(params) => (
                  <TextField {...params} label="User" size="small" placeholder="Filter by User" fullWidth InputLabelProps={{ shrink: true }} />
                )}
                fullWidth
                size="small"
                disablePortal
                sx={{ '& .MuiInputBase-root': { height: 40 } }}
              />
              
              {/* Teams filter */}
              <Autocomplete
                options={teams}
                value={teams.find(t => t.id === teamFilter) || null}
                onChange={(e, value) => setTeamFilter(value?.id || 'all')}
                getOptionLabel={(option) => option.name}
                renderInput={(params) => (
                  <TextField {...params} label="Team" size="small" placeholder="Filter by Team" fullWidth InputLabelProps={{ shrink: true }} />
                )}
                fullWidth
                size="small"
                disablePortal
                sx={{ '& .MuiInputBase-root': { height: 40 } }}
              />

              {/* Countries filter */}
              <Autocomplete
                options={countries}
                value={countries.find(c => c.id === countryFilter) || null}
                onChange={(e, value) => setCountryFilter(value?.id || 'all')}
                getOptionLabel={(option) => option.label || option.name}
                renderInput={(params) => (
                  <TextField {...params} label="Country" size="small" placeholder="Filter by Country" fullWidth InputLabelProps={{ shrink: true }} />
                )}
                fullWidth
                size="small"
                disablePortal
                sx={{ '& .MuiInputBase-root': { height: 40 } }}
              />
            </>
          )}
          
          {/* Clear Filters Button */}
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            onClick={() => {
              setFromDate(null);
              setToDate(null);
              setUserFilter('all');
              setTeamFilter('all');
              setCountryFilter('all');
            }}
            sx={{ 
              minWidth: 120,
              height: 40,
              flex: '0 0 auto !important'
            }}
          >
            Clear Filters
          </Button>
        </Stack>
      </Paper>

      {/* Excel-like Performance Table */}
      {isLoading ? (
        <Stack spacing={1}>
          {[...Array(6)].map((_, idx) => (
            <Skeleton key={idx} variant="rectangular" height={40} />
          ))}
        </Stack>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              {/* Total Counts Row */}
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell 
                  sx={{ 
                    fontWeight: 'bold', 
                    borderRight: 1, 
                    borderColor: 'divider',
                    position: 'sticky', 
                    left: 0, 
                    bgcolor: 'grey.50',
                    zIndex: 1,
                  }}
                >
                  Total Counts
                </TableCell>
                {/* Companies Created Column - Only show in company view */}
                {dataType === 'company' && (
                  <TableCell 
                    align="center"
                    sx={{ bgcolor: '#fef3c7', minWidth: 128, borderRight: 1, borderColor: 'divider' }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary', display: 'block' }}>
                      Companies Created
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 0.5 }}>
                      {performanceData?.totals?.['Companies Created'] || 0}
                    </Typography>
                  </TableCell>
                )}
                {filteredColumns.map((column, index) => (
                  <TableCell 
                    key={column} 
                    align="center"
                    sx={{ 
                      borderRight: 1, 
                      borderColor: 'divider',
                      bgcolor: getColumnColor(index),
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary', display: 'block' }}>
                      {column.split(" - ").slice(-1)[0]}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 0.5 }}>
                      {performanceData?.totals?.[column] || 0}
                    </Typography>
                  </TableCell>
                ))}
                <TableCell 
                  align="center"
                  sx={{ bgcolor: 'grey.100', minWidth: 128 }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary', display: 'block' }}>
                    {dataType === 'company' ? 'Total Companies' : 'Total Calls'}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 0.5 }}>
                    {performanceData?.totals?.total || 0}
                  </Typography>
                </TableCell>
              </TableRow>

              {/* Blank Row */}
              <TableRow>
                <TableCell colSpan={filteredColumns.length + (dataType === 'company' ? 3 : 2)} />
              </TableRow>

              {/* Header Row */}
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell 
                  sx={{ 
                    fontWeight: 'bold',
                    borderRight: 1,
                    borderColor: 'divider',
                    position: 'sticky',
                    left: 0,
                    bgcolor: 'grey.50',
                    zIndex: 1,
                  }}
                >
                  {(performanceData?.type || viewType).charAt(0).toUpperCase() + (performanceData?.type || viewType).slice(1)}
                </TableCell>
                {/* Companies Created Header - Only show in company view */}
                {dataType === 'company' && (
                  <TableCell 
                    align="center"
                    sx={{ fontWeight: 'bold', bgcolor: '#fef3c7', minWidth: 128, borderRight: 1, borderColor: 'divider' }}
                  >
                    Companies Created
                  </TableCell>
                )}
                {filteredColumns.map((column, index) => (
                  <TableCell 
                    key={column}
                    align="center"
                    sx={{
                      fontWeight: 'bold',
                      borderRight: 1,
                      borderColor: 'divider',
                      bgcolor: getColumnColor(index),
                      minWidth: 128
                    }}
                  >
                    {column.split(" - ").slice(-1)[0]}
                  </TableCell>
                ))}
                <TableCell 
                  align="center"
                  sx={{ fontWeight: 'bold', bgcolor: 'grey.100', minWidth: 128 }}
                >
                  {dataType === 'company' ? 'Total Companies' : 'Total Calls'}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Data */}
              {performanceData?.rows.map((row, idx) => (
                <TableRow 
                  key={(row.id || idx) + '-' + row.name}
                  sx={{ 
                    '&:hover': { bgcolor: 'grey.50' },
                    bgcolor: idx % 2 === 0 ? 'background.paper' : 'grey.25'
                  }}
                >
                  <TableCell 
                    sx={{ 
                      fontWeight: 500,
                      borderRight: 1,
                      borderColor: 'divider',
                      position: 'sticky',
                      left: 0,
                      bgcolor: 'background.paper',
                      zIndex: 1
                    }}
                  >
                    {row.name || 'Unassigned'}
                  </TableCell>
                  {/* Companies Created cell - Only show in company view */}
                  {dataType === 'company' && (
                    <TableCell 
                      align="center"
                      sx={{ borderRight: 1, borderColor: 'divider', fontWeight: 500 }}
                    >
                      {row['Companies Created'] || 0}
                    </TableCell>
                  )}
                  {filteredColumns.map(column => (
                    <TableCell 
                      key={column}
                      align="center"
                      sx={{ borderRight: 1, borderColor: 'divider', fontWeight: 500 }}
                    >
                      {row[column] || 0}
                    </TableCell>
                  ))}
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                    <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                      <span>{row.total}</span>
                      {dataType === 'call' && row.total > 0 && (
                        <IconButton 
                          size="small" 
                          onClick={() => handleViewDetails(row)}
                          sx={{ 
                            color: 'primary.main',
                            '&:hover': { bgcolor: 'primary.lighter' }
                          }}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              
              {/* No Data */}
              {performanceData?.rows.length === 0 && (
                <TableRow>
                  <TableCell 
                    colSpan={filteredColumns.length + (dataType === 'company' ? 3 : 2)}
                    align="center"
                    sx={{ py: 4, color: 'text.secondary' }}
                  >
                    No data available for the selected filters
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
              {/* Totals Row */}
              {/* {performanceData?.rows.length > 0 && (
                <TableFooter>
                  <TableRow sx={{ bgcolor: 'grey.100', fontWeight: 'bold', borderTop: 2, borderColor: 'divider' }}>
                    <TableCell 
                      sx={{ 
                        fontWeight: 'bold',
                        borderRight: 1,
                        borderColor: 'divider',
                        position: 'sticky',
                        left: 0,
                        bgcolor: 'grey.100',
                        zIndex: 1
                      }}
                    >
                      TOTAL
                    </TableCell>
                    {performanceData?.columns?.map(column => (
                      <TableCell 
                        key={column}
                        align="center"
                        sx={{ borderRight: 1, borderColor: 'divider', fontWeight: 'bold' }}
                      >
                        {performanceData?.totals?.[column] || 0}
                      </TableCell>
                    ))}
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                      {performanceData?.totals?.total || 0}
                    </TableCell>
                  </TableRow>
                </TableFooter>
              )} */}
          </Table>
        </TableContainer>
      )}

      {/* Results Count */}
      <Typography variant="body2" sx={{ color: 'text.secondary', px: 1 }}>
        Showing {performanceData?.rows?.length} {(performanceData?.type || viewType)}s
      </Typography>

      {/* Call Details Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        PaperProps={{
          sx: { width: { xs: '100%', sm: 450 } }
        }}
      >
        <Box sx={{ p: 2 }}>
          {/* Header */}
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" fontWeight="bold">
              Call Details - {selectedUser?.name}
            </Typography>
            <IconButton onClick={handleCloseDrawer} size="small">
              <Close />
            </IconButton>
          </Stack>
          <Divider sx={{ mb: 2 }} />

          {/* Loading State */}
          {loadingDetails && (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
              <CircularProgress />
              <Typography sx={{ mt: 2, color: 'text.secondary' }}>Loading call details...</Typography>
            </Stack>
          )}

          {/* Call Details Content */}
          {!loadingDetails && callDetails && (
            <Stack spacing={2}>
              {/* Summary */}
              <Paper sx={{ p: 2, bgcolor: 'primary.lighter' }}>
                <Typography variant="subtitle2" color="text.secondary">Total Calls</Typography>
                <Typography variant="h4" fontWeight="bold" color="primary.main">
                  {callDetails.total_calls}
                </Typography>
              </Paper>

              {/* Stage-wise breakdown */}
              {callDetails.stages?.map((stage) => (
                <Box key={stage.stage_name}>
                  <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                    <Chip 
                      label={stage.stage_name} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                    />
                    <Typography variant="body2" color="text.secondary">
                      ({stage.call_count} calls)
                    </Typography>
                  </Stack>
                  
                  {/* Companies in this stage */}
                  <Stack spacing={1} sx={{ pl: 2 }}>
                    {stage.companies?.map((company) => (
                      <Paper 
                        key={company.id} 
                        sx={{ 
                          p: 1.5, 
                          bgcolor: 'grey.50',
                          '&:hover': { bgcolor: 'grey.100' }
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Link
                              href={`/companies/${company.id}`}
                              target="_blank"
                              underline="hover"
                              sx={{ 
                                fontWeight: 500,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5
                              }}
                            >
                              {company.name}
                              <OpenInNew sx={{ fontSize: 14 }} />
                            </Link>
                            <Typography variant="caption" color="text.secondary">
                              {company.call_count} call{company.call_count !== 1 ? 's' : ''}
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                </Box>
              ))}

              {/* No data */}
              {callDetails.stages?.length === 0 && (
                <Typography color="text.secondary" textAlign="center" py={4}>
                  No call details available
                </Typography>
              )}
            </Stack>
          )}
        </Box>
      </Drawer>
    </Stack>
  );
}